import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Ladro disponibili al level-up nei livelli 2-5
 * (manuale V1, rr. 3811-3903). 16 mosse totali, alcune con prerequisiti.
 *
 * Catena prerequisiti notabili:
 *  - *Colpo Basso* richiede *Attacco Furtivo*
 *  - *Prudente* richiede *Esperto di Trappole*
 *  - *Alchimista* richiede *Avvelenatore*
 *
 * *Mascalzone Multiclasse* è una mossa multiclasse: il suo testo descrive
 * la possibilità di scegliere una mossa di un'altra classe (Guerriero/Bardo/
 * Ladro/Ranger/Sciamano). La regola cross-class vive nella description (D3
 * del WIP); nessun campo strutturato di MoveSchema la rappresenta oggi.
 */

const milleRisorse: Move = {
  id: 'ladro.mille-risorse',
  name: 'Mille Risorse',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Al level-up (livello 2-5)',
  description: 'Scegli una mossa iniziale del Ladro che non hai già preso.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede una mossa iniziale residua del gruppo del Ladro.
  grantsMoveChoice: { kind: 'from-group', groupId: 'mossa-iniziale-aggiuntiva' },
};

const colpoBasso: Move = {
  id: 'ladro.colpo-basso',
  name: 'Colpo Basso',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger:
    "Quando infliggi danni usando un'arma con etichetta *precisa* o con portata *corta*, se lo fai usando *Attacco Furtivo*",
  description: 'Infliggi +1d4 danni.',
  tags: [],
  prerequisites: ['ladro.attacco-furtivo'],
  replaces: [],
};

const schivata: Move = {
  id: 'ladro.schivata',
  name: 'Schivata',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando cerchi di schivare un attacco in mischia grazie alla tua superiore agilità',
  description:
    "Tira+**DES**. Con un 7-9, non subisci i danni dell'attacco. Con 10+, inoltre, scegli un'opzione:\n\n- riesci a passare alle spalle dell'attaccante, che ti perde di vista per qualche (cruciale) istante;\n- riesci ad allontanarti quanto basta per non essere più alla portata degli attacchi in mischia del tuo avversario;\n- fai infuriare il nemico che concentrerà le sue attenzioni su di te.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: "Non subisci i danni e scegli un'opzione vantaggiosa." },
    on7to9: { text: "Non subisci i danni dell'attacco." },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ioSparoPerPrimo: Move = {
  id: 'ladro.io-sparo-per-primo',
  name: 'Io Sparo per Primo',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando qualcuno o qualcosa ti sta per cogliere di sorpresa',
  description: 'Te ne accorgi appena in tempo e puoi agire prima di subire le conseguenze.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const passoFelpato: Move = {
  id: 'ladro.passo-felpato',
  name: 'Passo Felpato',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando ti nascondi alla vista per agire indisturbato',
  description:
    'Tira+**DES**. Con 7-9, finché mantieni un basso profilo e rimani celato, potrai muoverti e agire senza che nessuno ti noti. Con 10+, inoltre, le circostanze ti sono molto favorevoli (condizioni di luce, elementi scenici, distrazione dei presenti) e ottieni una presa da spendere per concretizzare una di queste opportunità. Se lo fai, puoi cogliere chiunque di sorpresa, ottenendo anche un +1 al prossimo tiro.',
  roll: {
    stat: 'destrezza',
    on10plus: {
      text: 'Ti muovi indisturbato + ottieni una presa per cogliere chiunque di sorpresa con +1 al prossimo tiro.',
    },
    on7to9: {
      text: 'Finché mantieni basso profilo e resti celato, ti muovi e agisci senza essere notato.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const prudente: Move = {
  id: 'ladro.prudente',
  name: 'Prudente',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando usi *Esperto di Trappole*',
  description: 'Hai sempre 1 presa in più, anche con 6-.',
  tags: [],
  prerequisites: ['ladro.esperto-di-trappole'],
  replaces: [],
};

const occhiScrutatori: Move = {
  id: 'ladro.occhi-scrutatori',
  name: 'Occhi Scrutatori',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando discerni la realtà',
  description:
    'Aggiungi le seguenti opzioni alla lista delle domande che puoi fare:\n\n- qual è il miglior modo per entrare, uscire o rimanere inosservati?\n- Chi è più vulnerabile o indifeso nei miei confronti?',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const maestroDeiVeleni: Move = {
  id: 'ladro.maestro-dei-veleni',
  name: 'Maestro dei Veleni',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Dopo che hai usato un tipo di veleno almeno una volta',
  description: 'Non sarà più *pericoloso* per te.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const alchimista: Move = {
  id: 'ladro.alchimista',
  name: 'Alchimista',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger:
    'Quando hai qualche ora di tempo e un posto sicuro per preparare un veleno che hai già usato almeno una volta in passato',
  description: 'Se hai gli ingredienti necessari (concordali con il GM), ne ottieni 3 dosi.',
  tags: [],
  prerequisites: ['ladro.avvelenatore'],
  replaces: [],
};

const armaAvvelenata: Move = {
  id: 'ladro.arma-avvelenata',
  name: 'Arma Avvelenata',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando spargi sulla tua arma un veleno che non è *pericoloso* per te',
  description:
    'Sai come usare anche i veleni più complessi senza sforzo. Consideralo *a contatto* invece che *applicato*.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sporchiTrucchetti: Move = {
  id: 'ladro.sporchi-trucchetti',
  name: 'Sporchi Trucchetti',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando fai ricorso ai tuoi *Sporchi Trucchetti*',
  description:
    "Hai con te un arsenale di bombe fumogene, triboli e altro (3 utilizzi, peso 1). Se lanci queste cose, la portata è *vicino*. Scegli uno dei seguenti effetti:\n\n- rallenti un numero limitato di avversari utilizzando triboli o roba del genere;\n- oscuri per qualche secondo una piccola area usando una bomba fumogena;\n- distrai qualcuno con petardi o arnesi simili.\n\nQuindi tira+**DES**. Con 10+, ottieni esattamente ciò che volevi e hai +1 alla prossima azione che possa beneficiare dell'effetto prodotto. Con 7-9, scegli anche uno dei seguenti:\n\n- lasci tracce che possono rivelare la tua presenza (puoi scegliere questa opzione solo se sei nascosto);\n- ti esponi a un pericolo (il GM ti dirà cosa);\n- sei costretto a spendere un utilizzo dei tuoi *Sporchi Trucchetti*;\n- l'effetto è parziale o ha luogo in maniera imprevista.\n\nOgni volta che hai tempo e modo di racimolare il materiale necessario a rimpolpare il tuo arsenale (a discrezione del GM), puoi ripristinare a 3 gli utilizzi dei tuoi *Sporchi Trucchetti*.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Effetto pieno +1 alla prossima azione che ne beneficia.' },
    on7to9: { text: 'Effetto parziale o con costo aggiuntivo (vedi lista).' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ingegneriaSociale: Move = {
  id: 'ladro.ingegneria-sociale',
  name: 'Ingegneria Sociale',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger:
    "Quando passi un po' di tempo a esplorare un centro abitato in cerca di opportunità e bersagli facili",
  description:
    'Tira+**CAR**. Con 10+, ottieni 3 prese. Con 7-9, ne prendi 2. Con 6-, prendi comunque 1 presa, ma anche il GM ne ottiene 1.\n\nSe il GM ha delle prese, può spenderne una in qualsiasi momento per introdurre una persona che ti è ostile, che sospetta di te, o verso cui sei in debito.\n\nTu, invece, puoi spendere una presa per chiedere un favore a qualcuno (un piccolo prestito, un compito semplice e non pericoloso, o simili). Non puoi spendere prese per chiedere favori a chi è sospettoso o apertamente ostile nei tuoi confronti (inclusi i personaggi degli altri giocatori, che ti guardano sempre con sospetto, sapendo con chi hanno a che fare).',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Ottieni 3 prese.' },
    on7to9: { text: 'Ottieni 2 prese.' },
    on6minus: { text: 'Ottieni 1 presa, ma anche il GM ne ottiene 1.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const malalingua: Move = {
  id: 'ladro.malalingua',
  name: 'Malalingua',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: "Quando passi un po' di tempo in un centro abitato a parlare degli ultimi pettegolezzi",
  description:
    "Tira+**CAR**. Con un 7-9, vieni a sapere di un'interessante opportunità nelle vicinanze. Con 10+, puoi anche dar vita a una diceria a tuo piacimento, che si diffonderà a macchia d'olio.",
  roll: {
    stat: 'carisma',
    on10plus: { text: "Conosci un'opportunità + diffondi una diceria a tua scelta." },
    on7to9: { text: "Conosci un'opportunità nelle vicinanze." },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const contatti: Move = {
  id: 'ladro.contatti',
  name: 'Contatti',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Quando metti in giro negli ambienti criminali la voce che ti serve qualcosa',
  description:
    'Specifica cosa (roba, informazioni, raccomandazioni, qualcuno per un lavoretto pericoloso e così via), e tira+**CAR**. Con 10+, qualcuno può farti avere esattamente ciò che cerchi. Con 7-9, dovrai accontentarti di qualcosa di simile, oppure avrai ciò che vuoi, ma a determinate condizioni.',
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Qualcuno può farti avere esattamente ciò che cerchi.' },
    on7to9: { text: 'Ottieni qualcosa di simile, oppure ciò che vuoi a condizioni.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const coltelloAllaGola: Move = {
  id: 'ladro.coltello-alla-gola',
  name: 'Coltello alla Gola',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: "Quando *parlamenti* usando l'incolumità fisica di qualcuno come leva",
  description: 'Puoi tirare, a tua scelta, +**DES** invece che +**CAR**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const mascalzoneMulticlasse: Move = {
  id: 'ladro.mascalzone-multiclasse',
  name: 'Mascalzone Multiclasse',
  type: 'advanced_2_5',
  classId: 'ladro',
  trigger: 'Al level-up (livello 2-5), come scelta multiclasse',
  description:
    "Mossa *multiclasse*. Scegli una mossa non multiclasse da un'altra classe. Ai fini della scelta, considera il tuo livello come fosse inferiore di uno.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto + cap livello -1.
  multiclass: {
    picks: { kind: 'any-other-class' },
    levelAdjustment: { delta: -1 },
  },
};

export const ladroAdvanced2to5Moves: readonly Move[] = [
  milleRisorse,
  colpoBasso,
  schivata,
  ioSparoPerPrimo,
  passoFelpato,
  prudente,
  occhiScrutatori,
  maestroDeiVeleni,
  alchimista,
  armaAvvelenata,
  sporchiTrucchetti,
  ingegneriaSociale,
  malalingua,
  contatti,
  coltelloAllaGola,
  mascalzoneMulticlasse,
] as const;
